import { useState } from "react";
import { DatabaseZap, ShieldCheck } from "lucide-react";
import { hasAnyRole, useAuth } from "../../auth";
import { api, ApiError } from "../../api";
import { Button, Card } from "../../ui";

type ImportResult = {
  inserted: number;
  updated: number;
  aliasesRemoved: number;
  aliasesDeactivated: number;
  total: number;
};

export function AdminContractorImport() {
  const { state } = useAuth();
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  if (
    state.status !== "authenticated" ||
    !hasAnyRole(state.me, ["admin"])
  ) {
    return (
      <Card>
        <p className="text-ink-500">
          Only Administrators can import the contractor directory.
        </p>
      </Card>
    );
  }

  const runImport = async () => {
    if (!confirmed || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const response = await api.post<{
        ok: true;
        data: ImportResult;
      }>("/api/admin/contractors/import", { confirm: true });
      setResult(response.data);
      setConfirmed(false);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setError(
          body?.error === "forbidden"
            ? "Your account is not authorized to run this import."
            : `Import failed: ${body?.error ?? "unknown error"}`,
        );
      } else {
        setError(`Import failed: ${String(err)}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <Card>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
            <DatabaseZap size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-ink-800 mb-1">
              Import contractor paper records
            </h2>
            <p className="text-sm text-ink-500 m-0">
              Load the 60-company contractor directory and sanitized job
              histories from the verified construction-request inventory.
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-lg border border-sand-200 bg-sand-50 p-4 text-sm text-ink-600">
          <div className="flex items-center gap-2 font-medium text-ink-700 mb-2">
            <ShieldCheck size={16} className="text-brand-600" />
            Safe to rerun
          </div>
          <ul className="m-0 pl-5 space-y-1">
            <li>Missing companies are inserted; existing companies are updated.</li>
            <li>Contact details and live project engagements are preserved.</li>
            <li>Unit numbers are excluded from imported job histories.</li>
            <li>The entire import succeeds or rolls back together.</li>
          </ul>
        </div>

        <label className="mt-5 flex items-start gap-2.5 text-sm text-ink-700 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            disabled={busy}
          />
          <span>
            I understand this will insert or refresh the contractor paper-record
            fields in this environment.
          </span>
        </label>

        {error && <div className="alert-error mt-4">{error}</div>}
        {result && (
          <div className="alert-success mt-4">
            Import complete: {result.inserted} inserted, {result.updated} updated,
            {" "}{result.aliasesRemoved} aliases removed, and{" "}
            {result.aliasesDeactivated} aliases deactivated ({result.total} source
            companies).
          </div>
        )}

        <div className="mt-5">
          <Button
            variant="primary"
            onClick={() => void runImport()}
            disabled={!confirmed}
            loading={busy}
          >
            Import contractor directory
          </Button>
        </div>
      </Card>
    </div>
  );
}